import { memo, useState, useRef, useEffect, useCallback } from 'react';
import debounce from 'lodash/debounce';

const DEBOUNCE_MS = 400;

interface DebouncedNumberInputProps {
    value: number | null;
    onChange: (value: number | null) => void;
    onExceedMax?: (val: number) => void;
    min?: number;
    max?: number;
    placeholder?: string;
    className?: string;
    'data-testid'?: string;
}

const toDisplayStr = (v: number | null) => (v != null ? String(v) : '');

/**
 * Controlled number input that buffers keystrokes in local state and
 * debounces the commit to the parent. Values outside [min, max] are
 * rejected immediately — the display stays at the previous value and
 * `onExceedMax` fires synchronously so the caller can show a toast.
 * Syncs back when the external `value` changes programmatically
 * (e.g. a form reset).
 */
const DebouncedNumberInput: React.FC<DebouncedNumberInputProps> = ({
    value,
    onChange,
    onExceedMax,
    min,
    max,
    placeholder,
    className = 'h-8 w-full px-3 border border-[#d9d9d9] rounded-md focus:outline-none text-sm',
    'data-testid': dataTestId,
}) => {
    const [localValue, setLocalValue] = useState<string>(toDisplayStr(value));

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const debouncedCommit = useRef(
        debounce((raw: string) => {
            const parsed = raw === '' ? null : Number(raw);
            onChangeRef.current(parsed);
        }, DEBOUNCE_MS),
    ).current;

    useEffect(() => () => debouncedCommit.cancel(), [debouncedCommit]);

    useEffect(() => {
        const externalStr = toDisplayStr(value);
        setLocalValue((prev) => {
            if (prev === '' && externalStr === '') return prev;
            const prevNumeric = prev === '' ? null : Number(prev);
            if (prevNumeric === value) return prev;
            return externalStr;
        });
    }, [value]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const raw = e.target.value;

            if (raw !== '') {
                const parsed = Number(raw);
                if (max != null && parsed > max) {
                    onExceedMax?.(parsed);
                    return;
                }
                if (min != null && parsed < min) return;
            }

            setLocalValue(raw);
            debouncedCommit(raw);
        },
        [debouncedCommit, min, max, onExceedMax],
    );

    return (
        <input
            type="number"
            data-testid={dataTestId}
            min={min}
            max={max}
            className={className}
            placeholder={placeholder}
            value={localValue}
            onChange={handleChange}
        />
    );
};

DebouncedNumberInput.displayName = 'DebouncedNumberInput';
export default memo(DebouncedNumberInput);
